import React from 'react';

interface Step {
  key: string;
  label: string;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
}

export function Stepper({ steps, currentStep, onStepClick }: StepperProps) {
  const edgeOffset = steps.length > 1 ? 50 / steps.length : 0;
  const fillSpan = 100 - edgeOffset * 2;
  const progress = steps.length > 1 ? currentStep / (steps.length - 1) : 0;

  return (
    <div className="stepper-wrapper mb-5">
      <div className="d-flex justify-content-between position-relative">
        {/* Progress line */}
        <div
          className="position-absolute bg-gray-200"
          style={{
            top: '20px',
            left: `${edgeOffset}%`,
            right: `${edgeOffset}%`,
            height: '2px',
            zIndex: 0
          }}
        />
        <div
          className="position-absolute bg-primary"
          style={{
            top: '20px',
            left: `${edgeOffset}%`,
            width: `${Math.min(100, progress * fillSpan)}%`,
            height: '2px',
            zIndex: 1,
            transition: 'width 0.3s ease'
          }}
        />

        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isActive = index === currentStep;
          const isClickable = onStepClick && index <= currentStep;

          return (
            <div
              key={step.key}
              className="d-flex flex-column align-items-center position-relative"
              style={{ zIndex: 2, cursor: isClickable ? 'pointer' : 'default', flex: 1, minWidth: 0 }}
              onClick={() => isClickable && onStepClick(index)}
            >
              <div
                className={`
                  d-flex align-items-center justify-content-center rounded-circle
                  ${isCompleted ? 'bg-primary text-white' : ''}
                  ${isActive ? 'bg-primary text-white' : ''}
                  ${!isCompleted && !isActive ? 'bg-white border border-gray-300 text-muted' : ''}
                `}
                style={{
                  width: '40px',
                  height: '40px',
                  transition: 'all 0.3s ease'
                }}
              >
                {isCompleted ? (
                  <i className="feather-check"></i>
                ) : (
                  <span className="fw-bold">{index + 1}</span>
                )}
              </div>
              <span
                className={`mt-2 small fw-semibold ${isActive ? 'text-primary' : 'text-muted'}`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface StepContentProps {
  children: React.ReactNode;
  title: string;
  description?: string;
}

export function StepContent({ children, title, description }: StepContentProps) {
  return (
    <div className="step-content">
      <div className="mb-4">
        <h4 className="fw-bold">{title}</h4>
        {description && <p className="text-muted">{description}</p>}
      </div>
      {children}
    </div>
  );
}
